import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { TextField, MenuItem } from "@mui/material";
import {
  useFetchUnitsForSelectionQuery,
  useCreateTertiaryUnitMutation,
  useUpdateUnitMutation,
} from "../../../../../services/baseQuery";
import { toast } from "react-hot-toast";

export const TertiaryUnitForm = ({ onClose, initialData, isEditing }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: initialData || {
      name: "",
      abbreviation: "",
      smallestUnit: "",
      largerUnit: "",
      parentUnit: "",
      unitsPerParent: "",
      unitsPerMiddle: "",
      description: "",
    },
  });

  const { data: unitsData } = useFetchUnitsForSelectionQuery();
  const [createUnit, { isLoading: isCreating }] =
    useCreateTertiaryUnitMutation();
  const [updateUnit, { isLoading: isUpdating }] = useUpdateUnitMutation();

  const isLoading = isCreating || isUpdating;

  // Get all units for larger and middle unit selection
  const baseUnits = unitsData?.data?.baseUnits || [];

  // Watch for changes in form fields
  const selectedParentUnit = watch("parentUnit");
  const selectedLargerUnit = watch("largerUnit");
  const selectedSmallestUnit = watch("smallestUnit");
  const unitsPerParent = watch("unitsPerParent");
  const unitsPerMiddle = watch("unitsPerMiddle");

  // Find unit details
  const parentUnitDetails =
    baseUnits.find(u => u._id === selectedParentUnit) || null;
  const largerUnitDetails =
    baseUnits.find(u => u._id === selectedLargerUnit) || null;
  const smallestUnitDetails =
    baseUnits.find(u => u._id === selectedSmallestUnit) || null;

  // Update the useEffect to handle the auto-generation more robustly
  useEffect(() => {
    if (
      parentUnitDetails &&
      largerUnitDetails &&
      smallestUnitDetails &&
      unitsPerParent &&
      unitsPerMiddle
    ) {
      try {
        const totalSmallUnits = Number(unitsPerParent) * Number(unitsPerMiddle);

        // Generate name and abbreviation
        const newName = `1${parentUnitDetails.name}/${unitsPerParent}${largerUnitDetails.name}/${totalSmallUnits}${smallestUnitDetails.name}`;

        const newAbbreviation = `${parentUnitDetails.abbreviation}/${unitsPerParent}${largerUnitDetails.abbreviation}/${totalSmallUnits}${smallestUnitDetails.abbreviation}`;

        setValue("name", newName);
        setValue("abbreviation", newAbbreviation);
      } catch (error) {
        console.error("Error generating unit names:", error);
      }
    }
  }, [
    parentUnitDetails,
    largerUnitDetails,
    smallestUnitDetails,
    unitsPerParent,
    unitsPerMiddle,
    setValue,
  ]);

  // When editing, we need to set up the form with existing values
  useEffect(() => {
    if (isEditing && initialData) {
      console.log("Initializing tertiary form with:", initialData);

      // If we're editing, we need to make sure the form is populated
      if (
        initialData.smallestUnit &&
        initialData.largerUnit &&
        initialData.parentUnit
      ) {
        // Set the values manually to ensure they're properly displayed
        setValue("name", initialData.name);
        setValue("abbreviation", initialData.abbreviation);
        setValue("smallestUnit", initialData.smallestUnit);
        setValue("largerUnit", initialData.largerUnit);
        setValue("parentUnit", initialData.parentUnit);
        setValue("unitsPerParent", initialData.unitsPerParent);
        setValue("unitsPerMiddle", initialData.unitsPerMiddle);
        setValue("description", initialData.description || "");
      }
    }
  }, [isEditing, initialData, setValue]);

  const onSubmit = async formData => {
    try {
      if (isEditing) {
        await updateUnit({
          id: initialData.id,
          data: {
            name: formData.name,
            abbreviation: formData.abbreviation,
            smallestUnitId: formData.smallestUnit,
            largerUnitId: formData.largerUnit,
            largestUnitId: formData.parentUnit,
            conversionFactorForLargerUnit: parseFloat(formData.unitsPerParent),
            conversionFactorForSmallestUnit:
              parseFloat(formData.unitsPerParent) *
              parseFloat(formData.unitsPerMiddle),
            description: formData.description,
          },
        }).unwrap();
        toast.success("Unit updated successfully");
      } else {
        await createUnit({
          name: formData.name,
          abbreviation: formData.abbreviation,
          smallestUnitId: formData.smallestUnit,
          largerUnitId: formData.largerUnit,
          largestUnitId: formData.parentUnit,
          conversionFactorForLargerUnit: parseFloat(formData.unitsPerParent),
          conversionFactorForSmallestUnit:
            parseFloat(formData.unitsPerParent) *
            parseFloat(formData.unitsPerMiddle),
          description: formData.description,
        }).unwrap();
        toast.success("Tertiary unit created successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error?.error || "Failed to save unit");
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.5rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E5E7EB",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
      <div className="grid gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            Unit Conversion Setup
          </h3>

          <div className="grid gap-4">
            {/* Largest Unit Selection */}
            <Controller
              name="parentUnit"
              control={control}
              rules={{ required: "Largest unit is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Largest Unit"
                  error={!!errors.parentUnit}
                  helperText={
                    errors.parentUnit?.message ||
                    "Select the largest unit (e.g., CARTON)"
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={inputStyles}>
                  {baseUnits.map(unit => (
                    <MenuItem
                      key={unit._id}
                      value={unit._id}
                      disabled={
                        unit._id === selectedLargerUnit ||
                        unit._id === selectedSmallestUnit
                      }>
                      {unit.displayName ||
                        `${unit.name} (${unit.abbreviation})`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Middle Unit Selection */}
            <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-start">
              <Controller
                name="largerUnit"
                control={control}
                rules={{ required: "Middle unit is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Middle Unit"
                    error={!!errors.largerUnit}
                    helperText={
                      errors.largerUnit?.message ||
                      "Select the middle unit (e.g., PACK)"
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={inputStyles}>
                    {baseUnits.map(unit => (
                      <MenuItem
                        key={unit._id}
                        value={unit._id}
                        disabled={
                          unit._id === selectedParentUnit ||
                          unit._id === selectedSmallestUnit
                        }>
                        {unit.displayName ||
                          `${unit.name} (${unit.abbreviation})`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <TextField
                type="number"
                label="Units Per Largest"
                {...register("unitsPerParent", {
                  required: "Required",
                  min: { value: 0.000001, message: "Must be greater than 0" },
                })}
                error={!!errors.unitsPerParent}
                helperText={errors.unitsPerParent?.message}
                variant="outlined"
                size="small"
                sx={{ ...inputStyles, width: "180px" }}
              />
            </div>

            {/* Smallest Unit Selection */}
            <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-start">
              <Controller
                name="smallestUnit"
                control={control}
                rules={{ required: "Smallest unit is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Smallest Unit"
                    error={!!errors.smallestUnit}
                    helperText={
                      errors.smallestUnit?.message ||
                      "Select the smallest unit (e.g., PAIR)"
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={inputStyles}>
                    {baseUnits.map(unit => (
                      <MenuItem
                        key={unit._id}
                        value={unit._id}
                        disabled={
                          unit._id === selectedParentUnit ||
                          unit._id === selectedLargerUnit
                        }>
                        {unit.displayName ||
                          `${unit.name} (${unit.abbreviation})`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <TextField
                type="number"
                label="Units Per Middle"
                {...register("unitsPerMiddle", {
                  required: "Required",
                  min: { value: 0.000001, message: "Must be greater than 0" },
                })}
                error={!!errors.unitsPerMiddle}
                helperText={errors.unitsPerMiddle?.message}
                variant="outlined"
                size="small"
                sx={{ ...inputStyles, width: "180px" }}
              />
            </div>
          </div>

          {/* Conversion Preview */}
          {largerUnitDetails &&
            parentUnitDetails &&
            smallestUnitDetails &&
            unitsPerParent &&
            unitsPerMiddle && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">
                  1 {parentUnitDetails.name} = {unitsPerParent}{" "}
                  {largerUnitDetails.name} ={" "}
                  {Number(unitsPerParent) * Number(unitsPerMiddle)}{" "}
                  {smallestUnitDetails.name}
                </p>
              </div>
            )}
        </div>

        {/* Generated Name and Abbreviation */}
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Generated Name"
            {...register("name")}
            disabled
            value={watch("name")}
            variant="outlined"
            size="small"
            fullWidth
            sx={inputStyles}
          />

          <TextField
            label="Generated Abbreviation"
            {...register("abbreviation")}
            disabled
            value={watch("abbreviation")}
            variant="outlined"
            size="small"
            fullWidth
            sx={inputStyles}
          />
        </div>

        <TextField
          label="Description (Optional)"
          {...register("description")}
          multiline
          rows={2}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          placeholder="Add any additional notes about this unit conversion"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={isLoading}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            isLoading ||
            !selectedLargerUnit ||
            !selectedParentUnit ||
            !selectedSmallestUnit ||
            !unitsPerParent ||
            !unitsPerMiddle
          }
          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            `${isEditing ? "Update" : "Create"} Tertiary Unit`
          )}
        </button>
      </div>
    </form>
  );
};
